import styled, { css } from 'styled-components';
import type { ThemeProps } from 'styled-components';
import type { AllColorType, SizeType, ThemeType } from '../../util/types';
import type { ButtonContentAlignType } from './types';
import {
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNC,
  getPolishedColors,
} from '../../util/styled';
import {
  BUTTON_TOGGLE,
  ICON_MATERIAL,
  ICON_STUDENT_BACKGROUND,
  ICON_STUDENT_BORDER,
  ICON_STUDENT_CHART,
  ICON_WRAPPER,
} from '../../util/data-id';

const getHighlightStyles = (props: ThemeProps<ThemeType>) => {
  const highlight = getPolishedColors({ theme: props.theme, $color: 'primary' });
  return css`
    background: ${props.theme.colors.white};
    color: ${props.theme.colors.primary};
    border-color: ${props.theme.colors.primary};
    border-width: 3px;
    
    [data-id='${ICON_MATERIAL}'],
    [data-id='${ICON_STUDENT_BACKGROUND}'],
    [data-id='${ICON_STUDENT_BORDER}'] {
      fill: ${props.theme.colors.primary};
    }
    
    &:hover {
      background: ${highlight.light.color};
    } 
  `;
};

const getLinkStyles = (props: ThemeProps<ThemeType> & { $color: AllColorType }) => {
  return css`
    text-decoration: underline;
    height: auto;
    padding: 0;
    display: inline-block;
    &, &:hover {      
      box-shadow: none;
      color: ${props.theme.colors[props.$color]};         
    }
    &:hover {
      text-decoration: none;
    }
  `;
};

const getFillStyles = (props: ThemeProps<ThemeType> & { $color: AllColorType, $highlight: boolean }) => {
  const colors = getPolishedColors(props);

  return css`
    background: ${colors.main.color};
    color: ${props.$color === 'transparent' ? props.theme.colors.dark : colors.main.readable};
    border-color: ${colors.main.color};

    [data-id='${ICON_MATERIAL}'],
    [data-id='${ICON_STUDENT_BACKGROUND}'],
    [data-id='${ICON_STUDENT_BORDER}'] {
      fill: ${props.$color === 'transparent' ? props.theme.colors.dark : colors.main.readable} !important;
    }

    &:hover {
      background: ${colors.most_light.color};
      color: ${colors.most_light.readable};

      [data-id='${ICON_MATERIAL}'],
      [data-id='${ICON_STUDENT_BACKGROUND}'],
      [data-id='${ICON_STUDENT_BORDER}'] {
        fill: ${props.$color === 'transparent' ? props.theme.colors.dark : colors.most_light.readable} !important;
      }
    }
  `;
};

const getOutlineStyles = (props: ThemeProps<ThemeType> & { $color: AllColorType }) => {
  const colors = getPolishedColors(props);
  return css`
    background: ${props.theme.colors.white};
    color: ${props.theme.colors.dark};
    border-color: ${colors.main.color};
    
    [data-id='${ICON_MATERIAL}'] {
      fill: ${props.theme.colors.dark};
    }
    
    [data-id='${ICON_STUDENT_BACKGROUND}'],
    [data-id='${ICON_STUDENT_BORDER}'] {
      fill: ${colors.main.color};
    }
    
    [data-id='${ICON_STUDENT_CHART}'] {
      [data-id='${ICON_STUDENT_BACKGROUND}'],
      [data-id='${ICON_STUDENT_BORDER}'] {
        fill: ${props.theme.colors.dark};
      }    
    }
    
    & > div > div {
      [data-id='${ICON_STUDENT_BACKGROUND}'],
      [data-id='${ICON_STUDENT_BORDER}'] {
        fill: ${colors.main.color};
      }
    }    
    
    .${BUTTON_TOGGLE} svg {
      fill: ${props.theme.colors.dark} !important;
    }

    &:hover {
      background: ${colors.most_light.color};
      color: ${props.theme.colors.dark};

      [data-id='${ICON_MATERIAL}'],
      [data-id='${ICON_STUDENT_BACKGROUND}'],
      [data-id='${ICON_STUDENT_BORDER}'] {
        fill: ${props.theme.colors.dark} !important;
      }
    }
  `;
};

const getPressedStyles = (props: ThemeProps<ThemeType> & { $color: AllColorType }) => {
  const colors = getPolishedColors(props);
  return css`
    &[aria-pressed='true'] {
      &:hover {
        background: ${colors.most_light.color};
        color: ${props.theme.colors.dark};      
  
        [data-id='${ICON_MATERIAL}'],
        [data-id='${ICON_STUDENT_BACKGROUND}'],
        [data-id='${ICON_STUDENT_BORDER}'] {
          fill: ${props.theme.colors.dark} !important;
        }
      }      
    }
  `;
};

const getGlobalStyles = (props: ThemeProps<ThemeType> & { $size: SizeType, $color: AllColorType, $isBold?: boolean, $hideShadow?: boolean }) => {
  return css`
    appearance: none !important;
    position: relative;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    cursor: pointer;
    font-size: ${props.theme.fonts[props.$size]};
    font-weight: ${props.$isBold ? 'bold' : 'normal'};                
    box-shadow: ${props.$color === 'transparent' ? 'none' : props.theme.shadows.md};
    transition: box-shadow ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC},
    background ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
    border: 2px solid transparent;
    outline: none;
    
    svg path {
      transition: fill ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};
    }
    
    &:disabled {
      cursor: not-allowed;
      background: ${props.theme.colors.light}!important;
      color: ${props.theme.colors.muted}!important;
      border-color: ${props.theme.colors.light}!important;
      &, &:hover {
        box-shadow: none;
      }
      [data-id='${ICON_MATERIAL}'],
      [data-id='${ICON_STUDENT_BACKGROUND}'],
      [data-id='${ICON_STUDENT_BORDER}'] {
        fill: ${props.theme.colors.muted}!important;
      }
    }
    
    ${!props.$hideShadow && `
      &:hover {
        box-shadow: ${props.theme.shadows.lg};
      }
   `}
    
    ${getPressedStyles(props)}
  `;
};

export const ButtonStyled = styled('button')<{
  $color: AllColorType,
  $highlight: boolean,
  $size: SizeType,
  $isOutline: boolean,
  $isPill: boolean,
  $isBold: boolean,
  $isToggle: boolean,
  $hasIcon: boolean,
  $align: ButtonContentAlignType,
  $asLink: boolean
}>`
  ${(props) => getGlobalStyles(props)}
  display: inline-flex;
  align-items: center;
  justify-content: ${(props) => props.$align};
  border-radius: ${(props) => props.$isPill ? props.theme.radius.circle : props.theme.radius.button};
  padding: ${(props) => `calc(${props.theme.sizes[props.$size]} / 6) calc(${props.theme.sizes[props.$size]} / 4)`};  
  
  [data-id='${ICON_WRAPPER}'],
  .${BUTTON_TOGGLE} {
    width: calc(${(props) => props.theme.sizes[props.$size]} / 2);
    height: calc(${(props) => props.theme.sizes[props.$size]} / 2);
  }

  [data-id='${ICON_WRAPPER}']:not(.${BUTTON_TOGGLE}) {
    margin-right: ${(props) => `calc(${props.theme.sizes[props.$size]} / 5)`};
  }
  .${BUTTON_TOGGLE} {
    margin-left: ${(props) => `calc(${props.theme.sizes[props.$size]} / 5)`};
  }

  ${(props) => (props.$asLink ? getLinkStyles(props) : props.$highlight ? getHighlightStyles(props) : props.$isOutline ? getOutlineStyles(props) : getFillStyles(props))}  
`;

export const ButtonIconStyled = styled('button')<{
  $color: AllColorType,
  $highlight: boolean,
  $size: SizeType,
  $isOutline: boolean,
  $isCircle: boolean,
  $hideShadow: boolean,
  $asLink: boolean,
  $hasPadding: boolean,
}>`
  ${(props) => getGlobalStyles(props)}
  border-radius: ${(props) => props.$isCircle ? props.theme.radius.button : props.theme.radius.default};
  padding: ${(props) => `${props.$hasPadding ? `calc(${props.theme.sizes[props.$size]} / 6)` : 0} ${`calc(${props.theme.sizes[props.$size]} / 6)`}`};
  ${(props) => (props.$asLink ? getLinkStyles(props) : props.$highlight ? getHighlightStyles(props) : props.$isOutline ? getOutlineStyles(props) : getFillStyles(props))}
`;

export const ButtonGroup = styled('div')`
  display: flex;
  button:nth-child(1) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }
  button:nth-child(2) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;    
  }  
`;
